import { useSnackbar } from 'notistack';
import { ChangeEvent, Dispatch, useEffect, useRef, useState } from 'react';
import { createSiteProcess } from '../../../../apicalls/process';
import { TSelectItem } from '../../../../types/common';
import { checkResData } from '../../../../utils/common';
import {
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue
} from 'react-hook-form';
import {
  CATEGORY_PARENT_SITE_PROCESS,
  CATEGORY_CHILD_SITE_PROCESS
} from '../../../../const/ProcessConst';
import {
  convertFormDateToApiDate,
  createSiteProcessAndSiteTaskEmployeeList,
  createSiteProcessFormProcessListPulldown,
  log
} from '../../../../utils';
import {
  TAddSiteProcessForm,
  TSiteProcessDetailData
} from '../../../../types/process';

export type TProps = {
  setIsOpen: Dispatch<boolean>;
  afterSubmit: (createdSiteProcessDetailData: TSiteProcessDetailData) => void;
  constructId: number;
  setValue: UseFormSetValue<any>;
  reset: UseFormReset<any>;
  isOpen: boolean;
  parentSiteProcessId?: number;
  isFirstCreate: boolean;
};

export const useHooks = (props: TProps) => {
  const {
    setIsOpen,
    afterSubmit,
    constructId,
    setValue,
    reset,
    isOpen,
    parentSiteProcessId,
    isFirstCreate
  } = props;

  // 送信可能フラグ 多重送信対策
  const isSubmitable = useRef(false);

  const submit = (form: TAddSiteProcessForm) => {
    if (!isSubmitable.current) return;

    isSubmitable.current = false;

    createSiteProcess(
      form.site_process_name,
      constructId,
      convertFormDateToApiDate(form.start_date) as string, // 開始日は必須項目なので文字列として扱う
      form.parent_site_process_id.value,
      form.user_id.value,
      convertFormDateToApiDate(form.end_date),
      form.planned_man_day,
      form.note
    ).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          setIsOpen(false);
          // 初回の工程表作成のみログ出力
          if (isFirstCreate) {
            log.info('init construct process: create new process');
          }
          afterSubmit(data.data);
        } else {
          isSubmitable.current = true;
        }
      });
    });
  };

  // radioボタンの設定
  const arrCategory = [
    { name: '大工程', value: CATEGORY_PARENT_SITE_PROCESS },
    { name: '小工程', value: CATEGORY_CHILD_SITE_PROCESS }
  ];

  // カテゴリ
  const [selectedCategory, setCategory] = useState(
    CATEGORY_PARENT_SITE_PROCESS
  );

  // 大工程選択リスト
  const [parentSiteProccessList, setParentSiteProccessList] = useState<
    TSelectItem[]
  >([]);

  // 担当者選択リスト
  const [employeeList, setEmployeeList] = useState<TSelectItem[]>([]);

  // カテゴリ変更時のハンドラ
  const handleChangeCategoryRadio = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedCategory = e.target.value;
    setCategory(selectedCategory);
    setValue('category', selectedCategory);
    // 大工程の場合は大工程のプルダウンをリセット
    if (selectedCategory === CATEGORY_PARENT_SITE_PROCESS) {
      reset({
        category: CATEGORY_PARENT_SITE_PROCESS,
        parent_site_process_id: {
          value: 0,
          label: '--'
        }
      });
    }
  };

  // キャンセル時の処理
  const cancelSubmit = () => {
    setIsOpen(false);
  };

  // 期間終了日付
  const [endDate, setEndDate] = useState('');

  // 期間終了日付変更時のハンドラ
  const handleChangeEndDatePicker = (e: any) => {
    setEndDate(e.target.value);
  };

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isOpen) {
      // 初期値セット
      setCategory(CATEGORY_PARENT_SITE_PROCESS);
      setEndDate('');
      reset({
        category: parentSiteProcessId
          ? CATEGORY_CHILD_SITE_PROCESS
          : CATEGORY_PARENT_SITE_PROCESS,
        parent_site_process_id: { value: 0, label: '--' },
        site_process_name: null,
        construct_id: { value: 0, label: '--' },
        user_id: { value: 0, label: '--' },
        start_date: null,
        end_date: null,
        planned_man_day: null,
        note: null
      });
      // 担当者選択プルダウン
      createSiteProcessAndSiteTaskEmployeeList(
        constructId,
        enqueueSnackbar
      ).then(employeeList => {
        setEmployeeList(employeeList);
      });
      // 工程一覧プルダウンの設定
      createSiteProcessFormProcessListPulldown(
        constructId,
        enqueueSnackbar
      ).then(list => {
        setParentSiteProccessList(list);
        // 親工程を指定されている時はラジオボタンの小工程を選択状態にして工程名に親工程をセットする
        if (parentSiteProcessId) {
          list.forEach(list => {
            if (list.value === parentSiteProcessId) {
              setCategory(CATEGORY_CHILD_SITE_PROCESS);
              reset({
                parent_site_process_id: list
              });
            }
          });
        }
      });

      // オープン時に送信可能にする
      isSubmitable.current = true;
    }
  }, [isOpen]);

  return {
    submit,
    cancelSubmit,
    arrCategory,
    selectedCategory,
    handleChangeCategoryRadio,
    parentSiteProccessList,
    employeeList,
    endDate,
    handleChangeEndDatePicker
  };
};
